import { useEffect, useMemo, useRef } from "react";
import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";

import type {
  SpecLineClearBrowserInput,
  SpecLineResetBrowserInput,
  SpecSelectionClearItemBrowserInput,
  SpecSelectionCreateBrowserInput,
  SpecSelectionDeleteBrowserInput,
  SpecSelectionItemBrowserInput,
  SpecSelectionPaidUpgradeBrowserInput,
  SpecSelectionResetBrowserInput,
  SpecSelectionUpdateBrowserInput,
} from "@/api/contracts/spec-editor";
import {
  specLineClear,
  specLineReset,
  specSelectionClearItem,
  specSelectionCreate,
  specSelectionDelete,
  specSelectionReset,
  specSelectionSetItem,
  specSelectionUpdate,
  specSelectionUpdatePaidUpgrade,
} from "@/api/functions/spec-editor";
import {
  editorMutationOptions,
  patchSelection,
  type EditorGuess,
  type EditorMutationResult,
  type EditorMutationVariables,
  type EditorSnapshot,
} from "@/hooks/spec-editor-cache";
import type { SpecLinesLocation, SpecLinesSelection } from "@/hooks/specifications";
import type { CatalogItemImageInput } from "@/lib/catalog-item-image";

// The Editor's selection verbs (#801): what #807's dialogs, #809's rows, #810's
// line menu and #811's Item Search island host call. Each runs through #797's
// harness (`editorMutationOptions`) with its own `ERRORS` key, and its result
// — the line whole, its selections in the stored order with each item's
// catalog face — lands through the one cache updater (ADR-0014 decisions 1–6).
//
// **Three verbs guess** (ADR-0014 decision 5 as amended; the selections
// rulings item 11): Select lays the picked item in, Remove Selected takes it
// off, and the paid-upgrade box ticks — each at once, rolled back exactly on
// failure, and overwritten by the result on success. Add Selections, Edit
// Selection, Delete, the menu's Clear and the line's Clear wait for the server.
//
// Each hook's variables are the verb's browser input, which is also its cache
// target — Select's add the picked item's face, for its guess alone. No hook
// touches the Active selection: the edit page owns it, and the dialogs, rows
// and menu set and clear it (#807, #809, #810) — Delete's confirm clears it on
// success, as the source's does. The source's Clear re-emit (the row with
// `itemId: null`, restored on failure, `actions.ts:97-211`) is #810's menu
// setting the row's ids at the click; the cache shows the cleared item.

/** Add Selections — a new empty selection, `Selection N` unless named. */
export function createSpecSelectionMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecSelectionCreateBrowserInput>(queryClient, {
    mutationFn: (data) => specSelectionCreate({ data }),
    error: "ADD_SELECTION",
  });
}

/** What a held line ⋮ row's hook takes: what it runs on the answer. */
export interface AnsweredMutationProps {
  onAnswer?: (result: EditorMutationResult) => void;
}

/**
 * A verb's mutation whose `onAnswer` runs as the write answers, **before** its
 * result lands in the cache — the donor's `onSuccessCb` order
 * (`actions.ts:16-62`, `:313-362`), which the line ⋮'s Add Selections and
 * Resets close their menu on (#810, #1084). It runs with the hook's own
 * options, so it still runs when the answer redraws the row away (#1084).
 */
function useAnsweredMutation<V extends EditorMutationVariables>(
  makeOptions: (
    queryClient: QueryClient,
  ) => UseMutationOptions<EditorMutationResult, Error, V, EditorSnapshot>,
  { onAnswer }: AnsweredMutationProps,
) {
  const queryClient = useQueryClient();
  const options = useMemo(() => makeOptions(queryClient), [makeOptions, queryClient]);
  const answered = useRef(onAnswer);
  useEffect(() => {
    answered.current = onAnswer;
  });
  return useMutation({
    ...options,
    onSuccess: (...args) => {
      answered.current?.(args[0]);
      return options.onSuccess?.(...args);
    },
  });
}

/** Add Selections, closing the line ⋮ on its answer (#810). */
export function useCreateSpecSelection(props: AnsweredMutationProps = {}) {
  return useAnsweredMutation(createSpecSelectionMutationOptions, props);
}

/** Edit Selection — name and note together. */
export function updateSpecSelectionMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecSelectionUpdateBrowserInput>(queryClient, {
    mutationFn: (data) => specSelectionUpdate({ data }),
    error: "UPDATE_SELECTION_DETAILS",
  });
}

export function useUpdateSpecSelection() {
  return useMutation(updateSpecSelectionMutationOptions(useQueryClient()));
}

/** Delete selection — the line goes with its last alive one; usage recounted. */
export function deleteSpecSelectionMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecSelectionDeleteBrowserInput>(queryClient, {
    mutationFn: (data) => specSelectionDelete({ data }),
    error: "DELETE_SELECTION",
  });
}

export function useDeleteSpecSelection() {
  return useMutation(deleteSpecSelectionMutationOptions(useQueryClient()));
}

/** Clear on a line — the item off every selection of the line. */
export function clearSpecLineMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineClearBrowserInput>(queryClient, {
    mutationFn: (data) => specLineClear({ data }),
    error: "CLEAR_LINE",
  });
}

export function useClearSpecLine() {
  return useMutation(clearSpecLineMutationOptions(useQueryClient()));
}

/** The item's face in the selection's own item shape — what the card or the
 *  item dialog already holds when it calls `onSelect(item)` (#798, #811). */
export type SelectionItemPreview = NonNullable<SpecLinesSelection["item"]>;

/** An item as a card or the item dialog hands it to `Select`. */
export interface PickedItem {
  id: number;
  name: string;
  modelNumber?: string | null;
  jdeItemNumber?: string | null;
  url?: string | null;
  isCore: boolean;
  isApproved: boolean;
  hasEiTiers?: boolean;
  eiTiers?: readonly unknown[];
  deletedAt?: string | null;
  manufacturer: { id: number; name: string };
  heroImage?: CatalogItemImageInput | null;
}

/**
 * The picked item in the selection's own item shape — what #801's Select lays
 * into the row until the answer lands (the donor's `addItemToSelectionAction`
 * payload, `ItemCard.tsx:432-452`).
 */
export function selectionItemPreview(item: PickedItem): SelectionItemPreview {
  return {
    id: item.id,
    name: item.name,
    modelNumber: item.modelNumber ?? null,
    jdeItemNumber: item.jdeItemNumber ?? null,
    url: item.url ?? null,
    isCore: item.isCore,
    isApproved: item.isApproved,
    hasEiTiers: item.hasEiTiers ?? (item.eiTiers?.length ?? 0) > 0,
    retired: item.deletedAt != null,
    manufacturer: { id: item.manufacturer.id, name: item.manufacturer.name },
    heroImage: item.heroImage
      ? {
          ...item.heroImage,
          renditions: item.heroImage.renditions ? [...item.heroImage.renditions] : undefined,
        }
      : null,
  };
}

/** Select's variables: the browser input, and the picked item's face for the
 *  guess alone — it never reaches the server. */
export interface SelectSpecSelectionItemVariables extends SpecSelectionItemBrowserInput {
  item: SelectionItemPreview;
  /** The card's Select, clicked on an Active selection whose item Set as Per
   *  Plan cleared: a failure leaves the selection with no item (see below). */
  itemClearedAtClick?: boolean;
}

/**
 * Select's guess: the picked item laid into the selection in every cached
 * view that holds its line — the id and the face, so the row never flashes
 * `No Item Selected` while the request is out. The category waits for the
 * result, which the server writes from the item.
 */
export const selectSpecSelectionItemGuess: EditorGuess<SelectSpecSelectionItemVariables> = (
  data,
  { lineId, selectionId, itemId, item },
) => patchSelection(data, lineId, selectionId, (selection) => ({ ...selection, itemId, item }));

/**
 * Select — the item onto the selection, its category written by the server;
 * optimistic, rolled back exactly on failure. **But a click on an item Set as
 * Per Plan cleared fails to no item:** the donor's rollback writes back the
 * Active selection's `itemId` (`Lines/actions.ts:108-113`), and Set as Per
 * Plan's emit nulled it (`LineMoreActions/actions.ts:455-458`) — so the row
 * reads `No Item Selected` until the page reads again (#911).
 */
export function selectSpecSelectionItemMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SelectSpecSelectionItemVariables>(queryClient, {
    mutationFn: ({ item: _item, itemClearedAtClick: _cleared, ...data }) =>
      specSelectionSetItem({ data }),
    error: "SELECT_ITEM",
    guess: selectSpecSelectionItemGuess,
    failed: (data, variables) =>
      variables.itemClearedAtClick ? removeSelectedItemGuess(data, variables) : data,
  });
}

export function useSelectSpecSelectionItem() {
  return useMutation(selectSpecSelectionItemMutationOptions(useQueryClient()));
}

/**
 * Where a clear was clicked. **One verb, two call sites, deliberately split**
 * (ADR-0014 decision 5 as amended; the selections rulings item 1): the line
 * menu's `Clear` waits for the server and fails with `CLEAR_SELECTION`; the
 * card's and the item dialog's `Remove Selected` takes the item off at once
 * and fails with `REMOVE_SELECTED_ITEM`. The same server function and route
 * sit behind both, so the data never drifts between the two names — keep the
 * split as it is, either way.
 */
export type ClearSelectionItemCallSite = "menu" | "card";

/** Remove Selected's guess: the selection with no item, where it sits. */
export const removeSelectedItemGuess: EditorGuess<SpecSelectionClearItemBrowserInput> = (
  data,
  { lineId, selectionId },
) =>
  patchSelection(data, lineId, selectionId, (selection) => ({
    ...selection,
    itemId: null,
    item: null,
  }));

/** Clear and Remove Selected — the item pointer nulled; optimism by call site. */
export function clearSpecSelectionItemMutationOptions(
  queryClient: QueryClient,
  callSite: ClearSelectionItemCallSite,
) {
  return editorMutationOptions<SpecSelectionClearItemBrowserInput>(queryClient, {
    mutationFn: (data) => specSelectionClearItem({ data }),
    ...(callSite === "menu"
      ? { error: "CLEAR_SELECTION" }
      : { error: "REMOVE_SELECTED_ITEM", guess: removeSelectedItemGuess }),
  });
}

export function useClearSpecSelectionItem(callSite: ClearSelectionItemCallSite) {
  return useMutation(clearSpecSelectionItemMutationOptions(useQueryClient(), callSite));
}

/**
 * Set as Paid Upgrade's guess: the selection's flag, in place. The stored
 * order (paid upgrades last) comes with the result, which wins (ADR-0014
 * break 3).
 */
export const updateSpecSelectionPaidUpgradeGuess: EditorGuess<
  SpecSelectionPaidUpgradeBrowserInput
> = (data, { lineId, selectionId, isPaidUpgrade }) =>
  patchSelection(data, lineId, selectionId, (selection) => ({ ...selection, isPaidUpgrade }));

/** Set as Paid Upgrade — the selection's flag; optimistic, the response wins. */
export function updateSpecSelectionPaidUpgradeMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecSelectionPaidUpgradeBrowserInput>(queryClient, {
    mutationFn: (data) => specSelectionUpdatePaidUpgrade({ data }),
    error: "UPDATE_LINE_IS_PAID_UPGRADE",
    guess: updateSpecSelectionPaidUpgradeGuess,
  });
}

export function useUpdateSpecSelectionPaidUpgrade() {
  return useMutation(updateSpecSelectionPaidUpgradeMutationOptions(useQueryClient()));
}

// ---------------------------------------------------------------------------
// The line ⋮'s Resets (#1084)
// ---------------------------------------------------------------------------

/**
 * The rooms a Line Reset created, added to the session's list where it is
 * held: the answer carries the version's rooms only when it made one.
 */
export const resetSpecLineRooms = (
  rooms: SpecLinesLocation[],
  { locations }: EditorMutationResult,
): SpecLinesLocation[] => {
  const added = (locations ?? []).filter(({ id }) => !rooms.some((room) => room.id === id));
  return added.length > 0 ? [...rooms, ...added] : rooms;
};

/**
 * Reset to Base / to Core on a line — the whole line from its base line. Not
 * optimistic, and structural: the source's answer clears every other term's
 * ordering (`clearSearchCache`, Falcon `lines.py:518-588`).
 */
export function resetSpecLineMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineResetBrowserInput>(queryClient, {
    mutationFn: (data) => specLineReset({ data }),
    error: "RESET_LINE",
    structural: true,
    settleRooms: resetSpecLineRooms,
  });
}

export function useResetSpecLine(props: AnsweredMutationProps = {}) {
  return useAnsweredMutation(resetSpecLineMutationOptions, props);
}

/** Reset to Base / to Core on a selection row. Not optimistic. */
export function resetSpecSelectionMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecSelectionResetBrowserInput>(queryClient, {
    mutationFn: (data) => specSelectionReset({ data }),
    error: "RESET_SELECTION",
  });
}

export function useResetSpecSelection(props: AnsweredMutationProps = {}) {
  return useAnsweredMutation(resetSpecSelectionMutationOptions, props);
}
